import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type { UserInfo } from '@/types/deepadmin/UserInfo';
import type { UpdateUserPayload } from '@/types/deepadmin/payloads/UpdateUserPayload';
import type { Company } from '@/types/deepadmin/Company';
import { obtainMobileIdStatus } from '@/api/deepadmin/mobileid-check.ts';
import { useApi } from '@/composables/useApi.ts';
import { useAppLocale } from '@/composables/useAppLocale.ts';
import AuthService from '@/services/authService.ts';
import type { EmailCheckPayload } from '@deepcloud/api-deep-admin';

export const useDeepAdminStore = defineStore('deepadmin', () => {
  const { changeLocale } = useAppLocale();
  const { deepAdminBackendAPI, deepAdminAPI } = useApi();

  // User Info
  const fetchUserInfoPending = ref(false);
  const storeUserLanguagePending = ref(false);
  const updateUserPending = ref(false);
  const userInfo: Ref<UserInfo | null> = ref(null);
  const userLang = ref('');
  const checkMailRefPending = ref(false);
  const emailHasAccount = ref(false);

  async function fetchUserInfo() {
    try {
      fetchUserInfoPending.value = true;
      const response = await deepAdminAPI.value.usersMe.get();
      userInfo.value = response.data as UserInfo;
      if (response.data?.lang) {
        changeLocale(response.data.lang);
      }
      fetchUserInfoPending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      fetchUserInfoPending.value = false;
      return Promise.reject(error);
    }
  }

  async function storeUserLanguage(defaultLanguage: string) {
    try {
      const userId = AuthService.user?.sub;
      if (!userId) return;
      storeUserLanguagePending.value = true;
      const response =
        await deepAdminBackendAPI.value.usersSettingsV2.updateById(userId, {
          defaultLanguage
        });
      if (response && response.status === 204) {
        userLang.value = defaultLanguage;
      }
      storeUserLanguagePending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      storeUserLanguagePending.value = false;
      return Promise.reject(error);
    }
  }

  async function updateUser(userId: string, payload: UpdateUserPayload) {
    try {
      updateUserPending.value = true;

      const response = await deepAdminBackendAPI.value.usersV2.updateById(
        userId,
        {
          ...payload
        }
      );
      updateUserPending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      updateUserPending.value = false;
      return Promise.reject(error);
    }
  }

  const settingsSelectableOrganizations = computed(() => {
    const companies = userInfo.value?.companies;
    if (!companies) {
      return new Array<Company>();
    }

    const verifiedCompanies = companies
      .filter(
        (org) =>
          ['strong', 'weak'].includes(org.verification_state) &&
          org.roles.includes('MANAGE_COMPANY')
      )
      .sort((a, b) => a.display_name.localeCompare(b.display_name));

    const orgs = new Array<Company>();
    const personalSpaces = new Array<Company>();

    for (const company of verifiedCompanies) {
      if (company.structure === 'personal') {
        personalSpaces.push(company);
      } else {
        orgs.push(company);
      }
    }

    return [...orgs, ...personalSpaces];
  });

  const personalSpaceId = computed(() => {
    return userInfo.value?.companies.find((org) => org.structure === 'personal')
      ?.group_id;
  });

  // Mail ref
  async function checkMailRef(payload: EmailCheckPayload) {
    if (!payload.key || !payload.email) return;
    try {
      checkMailRefPending.value = true;
      const response = await deepAdminAPI.value.usersCheckEmail.create(
        payload,
        {
          progress: false
        }
      );
      checkMailRefPending.value = false;
      if (response.data) {
        emailHasAccount.value = response.data.is_valid;
      }
      return Promise.resolve(response);
    } catch (error) {
      checkMailRefPending.value = false;
      return Promise.reject(error);
    }
  }

  // Mobile ID
  const checkMobileIDPending = ref(false);

  async function checkMobileID(payload: string) {
    try {
      checkMobileIDPending.value = true;
      const body = { phone: payload };
      const response = await obtainMobileIdStatus(body);
      // const response = await deepAdminAPI.value.;
      checkMobileIDPending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      checkMobileIDPending.value = false;
      return Promise.reject(error);
    }
  }

  return {
    // User Info
    fetchUserInfoPending,
    settingsSelectableOrganizations,
    storeUserLanguagePending,
    updateUserPending,
    userInfo,
    userLang,
    personalSpaceId,
    fetchUserInfo,
    storeUserLanguage,
    updateUser,

    // Mail Ref
    checkMailRefPending,
    checkMailRef,
    emailHasAccount,

    // Mobile ID
    checkMobileIDPending,
    checkMobileID
  };
});
